import React, { useState } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { SearchType } from 'helpers/Enums';
import { ITicketOrder } from '../../../TicketDetailsContext';

interface IAddOrdersContext {
    searchType: string
    isSearching: boolean
    isSearchDone: boolean
    searchOrder: any
    searchRequestSchema: any
    orders: IOrder[]
    workOrders: IWorkOrder[]
    recordCount: number

    doSearch: (values: any) => void
    handleChangePage: (event: any, page: number) => void
    onSelect: (order: ITicketOrder) => void
    resetSearch: () => void
}

interface IOrder {
    orderId: number
    orderNumber: string
    orderType: string
    orderDate: moment.Moment
    orderStatus: string
    customerId: number
    customerName: string
    totalUom: string
    amountPaid: number
    hasPaymentInfo: boolean
    isShippable: boolean
    isShippingStarted: boolean
    isShippingCompleted: boolean
    isPaid: boolean
    isAdvancePaymentOk: boolean
    customerCountry: string
    layoutNumber: string
    total: number
    quotes: IQuote[]
}

interface IQuote {
    title: string
    quoteId: number
    quoteNumber: number
    quoteRevision: number
}

interface IWorkOrder {
    orderRowId: number
    orderDate?: moment.Moment
    orderRowStatus: string
    workOrder: string
    workOrderSub: string
    articleId: string
    articleDescription: string
    articleModel: string
    articleVersion: string
    quantity?: number
    orderNumber: string
    orderId: number
    customerId: number
    customerName: string
    customerCountry: string
    upmNotes: string
}

interface IProps {
    searchType: string
    customerId: number
    children: React.ReactNode
    onSelect: (selectedOrder: ITicketOrder) => void
}

export let addOrdersContext = React.createContext({} as IAddOrdersContext);

let { Provider } = addOrdersContext;

let AddOrdersProvider: React.FC<IProps> = ({ searchType, customerId, onSelect, children }) => {

    let searchRequestSchema = yup.object().shape({
        searchType: yup.string().default(searchType || 'orders'),
        customerId: yup.number().default(customerId),

        orderType: yup.string().default(''),
        orderNumber: yup.string().default(''),
        workOrder: yup.string().default(''),
        articleId: yup.string().default(''),
        articleModel: yup.string().default(''),

        pageNr: yup.number().default(0),
        pageSize: yup.number().default(15)
    }).required()

    type SearchRequest = yup.InferType<typeof searchRequestSchema>;

    let [isSearching, setSearching] = useState(false);
    let [isSearchDone, setSearchDone] = useState(false);
    let [searchOrder, setSearchOrder] = useState<SearchRequest>(searchRequestSchema.getDefault());

    let [orders, setOrders] = useState<IOrder[]>([]);
    let [workOrders, setWorkOrders] = useState<IWorkOrder[]>([]);
    let [recordCount, setRecordCount] = useState(0);


    let doSearch = (values: SearchRequest) => {
        let request = { ...values };

        if (values.pageNr > 0)
            request.pageNr = 0;

        searchOrders(request);
    }

    let searchOrders = async (values: SearchRequest) => {
        try {
            setSearching(true);
            setSearchDone(false);
            setSearchOrder(values);

            let res = values.searchType === SearchType.Orders
                ? await axios.post('/api/Order/SearchOrders', values)
                : await axios.post('/api/Order/SearchWorkOrders', values);

            let { items, recordCount } = res.data;

            setRecordCount(recordCount);

            if (values.searchType === SearchType.Orders) {
                setWorkOrders([]);
                setOrders(items);
            } else {
                setOrders([]);
                setWorkOrders(items);
            }

            setSearchDone(true);

        } catch (err) { }
        finally {
            setSearching(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...searchOrder,
            pageNr: page
        }

        window.scrollTo(0, 0);
        searchOrders(request);
    }

    let resetSearch = () => {
        setSearchOrder(searchRequestSchema.getDefault());
        setOrders([]);
        setWorkOrders([]);
        setSearchDone(false);
    }

    return (
        <Provider value={{
            searchType,
            searchOrder,
            searchRequestSchema,
            orders,
            workOrders,

            isSearching,
            isSearchDone,
            recordCount,

            doSearch,
            handleChangePage,
            onSelect,
            resetSearch
        }}>
            {children}
        </Provider>
    );
}

export default AddOrdersProvider;